import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can, inboxApi, useAuth, useInboxHub,
  NOTIFICATION_DEFAULTS, NOTIFICATION_KIND, PERMISSION,
  type ConnectionState, type InboxCacheEvent, type InboxEvent, type NotificationPreferences,
} from '@wa/shared';
import { playChime } from '@/notifications/chime';
import { permissionState, showPopup, type PermissionState } from '@/notifications/desktop';
import { setTabBadge } from '@/notifications/badge';

/**
 * One live connection for the whole app, and the three surfaces that hang off it.
 *
 * <b>The hub moved out of the inbox, and that was the change that made notifications
 * possible at all.</b> `useInboxHub` was called from `InboxPage`, so the connection
 * existed exactly while an agent was looking at the chat screen — which is the one
 * moment they do not need to be told a message arrived. An agent writing a report, in
 * Settings, or reading the campaign list had no connection at all, and a notification
 * system that only works on the screen you are already watching is decoration.
 *
 * So the connection lives in the shell, and the inbox <i>subscribes</i> to it. That
 * also closes a latent bug the old shape was one component away from: two callers of
 * `useInboxHub` would have opened two websockets, each with its own reconnect loop
 * and its own reconnect refetch storm.
 *
 * <b>Server decides whether you hear about it; the browser decides how.</b> An agent
 * with mentions switched off is not in the audience, so the event never reaches this
 * code. Whether what does reach it becomes a sound, a popup, both or neither is
 * settled here, from their own preferences — because two agents in the same audience
 * can want it presented differently, and sending the event three ways for three
 * agents would put a presentation concern in the database.
 */

interface Realtime {
  state: ConnectionState;
  /**
   * Returns its own unsubscribe. Subscribers get the cache events only — a
   * notification is consumed by the shell, and a screen should not have to write a
   * branch rejecting one.
   */
  subscribe: (handler: (event: InboxCacheEvent) => void) => () => void;
  subscribeResumed: (handler: () => void) => () => void;
  /** The conversation on screen, or null. Used to stay quiet about it. */
  setActiveConversation: (id: number | null) => void;
  preferences: NotificationPreferences;
  permission: PermissionState;
  /** Re-reads the browser's permission after a prompt somewhere else has answered it. */
  refreshPermission: () => void;
  /**
   * How many test notifications have reached this tab.
   *
   * The Notifications screen watches this number, and it exists because "Sent" is a
   * statement about an HTTP request and nothing else. Four separate things have to
   * work before a popup appears — the API, the websocket, the browser's permission
   * and the operating system — and until this existed, all four failed identically:
   * silence. Watching the event arrive splits that in half. If the number moves and
   * there was no popup, everything up to the browser worked and the problem is the
   * OS or Chrome's own notification setting. If it does not move, the popup was never
   * the problem.
   */
  testsSeen: number;
}

const Context = createContext<Realtime | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [permission, setPermission] = useState<PermissionState>(() => permissionState());
  const [testsSeen, setTestsSeen] = useState(0);

  const listeners = useRef(new Set<(event: InboxCacheEvent) => void>());
  const resumedListeners = useRef(new Set<() => void>());
  const activeConversation = useRef<number | null>(null);

  const signedIn = user !== null && user !== undefined;

  const preferencesQuery = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: inboxApi.notificationPreferences,
    enabled: signedIn,
    // Rarely changes, and a change made in another tab arrives as an event rather
    // than by polling — see the 'notification.preferences' branch below.
    staleTime: 10 * 60_000,
  });

  const preferences = preferencesQuery.data ?? NOTIFICATION_DEFAULTS;

  /*
   * The badge's number.
   *
   * Conversations with something unread, not notifications received — and that is
   * the whole reason this feature needs no notifications table. A popup is
   * ephemeral by nature: one raised while a laptop was asleep is gone, because
   * SignalR keeps no backlog. If the badge counted popups it would be wrong after
   * every reload and there would have to be a durable log behind it. Counting
   * unread conversations instead means the number is whatever the database says,
   * survives a reload, agrees with the inbox's own filter, and is already correct
   * after an hour offline.
   *
   * Same query key as the inbox's, so on the chat screen this is not a second
   * request — React Query serves both from one fetch.
   */
  const mayReadInbox = can(user, PERMISSION.inboxView);

  const counts = useQuery({
    queryKey: ['counts'],
    queryFn: inboxApi.counts,
    enabled: signedIn && mayReadInbox,
  });

  const unread = counts.data?.unread ?? 0;

  useEffect(() => {
    setTabBadge(preferences.badge ? unread : 0);
  }, [unread, preferences.badge]);

  // On the way out: a signed-out tab left showing "(7)" in the window list is
  // stating something about an account nobody is signed in to.
  useEffect(() => () => setTabBadge(0), []);

  /*
   * A notification arrived. Decide whether to make a noise about it.
   *
   * The one rule worth stating on its own: <b>nothing happens for the conversation
   * already on screen in a focused tab.</b> Being told about the message you are
   * looking at is the single fastest way to make somebody switch notifications off,
   * and it is the failure every chat app that gets this wrong has in common.
   *
   * Everything else does fire, including for another conversation while the inbox is
   * open — a quiet chime for a chat two rows down is the point of having a chime.
   */
  const announce = useCallback((event: Extract<InboxEvent, { type: 'notification' }>) => {
    const visible = document.visibilityState === 'visible';

    const looking = visible
      && document.hasFocus()
      && event.conversationId != null
      && event.conversationId === activeConversation.current;

    if (looking) return;

    /*
     * One sound, from whichever layer can actually produce it.
     *
     * The chime is this page's own audio, and a browser throttles a background tab's
     * audio before it throttles anything else — so on a minimised window it is the
     * one thing that cannot be relied on. The operating system's own notification
     * sound has no such problem, and is the right source exactly when the app is not
     * on screen. So the two swap places at the visibility boundary rather than one of
     * them always winning:
     *
     *   visible → our chime, and the notification is silent
     *   hidden  → no chime, and the notification carries the OS sound
     *
     * Never both, and — the part that matters — never neither, which is what a
     * hardcoded `silent: true` produced for every minimised window.
     */
    if (preferences.sound && visible) playChime();

    if (preferences.desktop) {
      showPopup({
        title: event.title,
        body: event.body,
        tag: `wa:${event.groupKey}`,
        silent: visible || !preferences.sound,
        onClick: event.url ? () => navigate(event.url!) : undefined,
      });
    }
  }, [navigate, preferences.desktop, preferences.sound]);

  /*
   * Fan-out.
   *
   * Held in a ref and read through it, so adding a subscriber does not re-create the
   * hub's handler and a new handler does not re-run the connection effect — which
   * would drop and rebuild the websocket every time the inbox re-rendered.
   */
  const onEvent = useCallback((event: InboxEvent) => {
    if (event.type === 'notification') {
      announce(event);

      // Counted before anything can swallow it, so the Notifications screen can say
      // "it reached this tab" independently of whether a popup was drawn.
      if (event.kind === NOTIFICATION_KIND.test) setTestsSeen((n) => n + 1);
      // Also a cache signal for everything that is not a message: an assignment, a
      // mention and an SLA warning all change what the inbox should be showing, and
      // the shell is the only thing listening when the agent is elsewhere. A new
      // message already has its own `message.received`, so it is left alone here
      // rather than invalidating twice.
      if (event.kind !== NOTIFICATION_KIND.newMessage && event.kind !== NOTIFICATION_KIND.test) {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['counts'] });
      }
      return;
    }

    if (event.type === 'notification.preferences') {
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      return;
    }

    // The badge and any screen that is not the inbox. The inbox does its own, far
    // more precise, handling through its subscription below.
    if (event.type === 'message.received') {
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
    }

    for (const handler of listeners.current) handler(event);
  }, [announce, queryClient]);

  const onResumed = useCallback(() => {
    // The shell's own stale data first — the badge is on screen on every page, and
    // a resumed connection means time passed with nothing listening.
    void queryClient.invalidateQueries({ queryKey: ['counts'] });
    for (const handler of resumedListeners.current) handler();
  }, [queryClient]);

  // Used directly, not mirrored into state. A `useState` + effect copy of it was one
  // extra render per connection change for no benefit, and the hook already holds the
  // authoritative value.
  const state = useInboxHub(signedIn, onEvent, onResumed);

  /*
   * The three functions are stable, and that is not tidiness.
   *
   * `useRealtimeEvents` subscribes in an effect keyed on `subscribe`. Defined inline
   * in the memo below, they would be new identities every time the memo recomputed —
   * which is every time the preferences load or change — so every screen listening to
   * the hub would unsubscribe and resubscribe for a setting it does not read. Empty
   * deps because all three only touch refs.
   */
  const subscribe = useCallback((handler: (event: InboxCacheEvent) => void) => {
    listeners.current.add(handler);
    return () => { listeners.current.delete(handler); };
  }, []);

  const subscribeResumed = useCallback((handler: () => void) => {
    resumedListeners.current.add(handler);
    return () => { resumedListeners.current.delete(handler); };
  }, []);

  const setActiveConversation = useCallback((id: number | null) => {
    activeConversation.current = id;
  }, []);

  const refreshPermission = useCallback(() => setPermission(permissionState()), []);

  const value = useMemo<Realtime>(() => ({
    state,
    subscribe,
    subscribeResumed,
    setActiveConversation,
    preferences,
    permission,
    refreshPermission,
    testsSeen,
  }), [state, subscribe, subscribeResumed, setActiveConversation,
       preferences, permission, refreshPermission, testsSeen]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Everything the shell knows about the live connection. Throws outside the shell. */
export function useRealtime(): Realtime {
  const value = useContext(Context);
  if (!value) throw new Error('useRealtime must be used inside RealtimeProvider.');
  return value;
}

/**
 * Subscribe to the shell's connection. The drop-in replacement for `useInboxHub`,
 * with the difference that ten callers share one websocket.
 *
 * The handlers are held in refs and the effect depends on neither, so a screen may
 * pass a fresh closure every render — as the inbox does, because its handler closes
 * over the selected conversation — without resubscribing on each one.
 */
export function useRealtimeEvents(
  onEvent: (event: InboxCacheEvent) => void,
  onResumed?: () => void,
): ConnectionState {
  const { state, subscribe, subscribeResumed } = useRealtime();

  const event = useRef(onEvent);
  event.current = onEvent;
  const resumed = useRef(onResumed);
  resumed.current = onResumed;

  useEffect(() => subscribe((e) => event.current(e)), [subscribe]);
  useEffect(() => subscribeResumed(() => resumed.current?.()), [subscribeResumed]);

  return state;
}

/**
 * Tells the shell which conversation is on screen, so it stays quiet about that one.
 *
 * Cleared on unmount, which matters: without it, navigating from a chat to Reports
 * would leave the shell believing that conversation was still being watched, and
 * every message in it would arrive in silence.
 */
export function useActiveConversation(id: number | null): void {
  const { setActiveConversation } = useRealtime();

  useEffect(() => {
    setActiveConversation(id);
    return () => setActiveConversation(null);
  }, [id, setActiveConversation]);
}
